import {Injectable} from '@angular/core';
import {
    confirmResetPassword,
    confirmSignIn,
    fetchAuthSession,
    getCurrentUser,
    resetPassword,
    signIn,
    signInWithRedirect,
    signOut,
    type SignInOutput
} from 'aws-amplify/auth';
import {Hub} from 'aws-amplify/utils';
import {BehaviorSubject, catchError, firstValueFrom, from, Observable, of, switchMap, tap} from 'rxjs';
import {UserService} from '../common/services/user.service';
import {User} from '../common/models/user';
import {environment} from '../../environments/environment';
import {Router} from "@angular/router";
import {SubtenantService} from '../common/services/subtenant.service';
import {AuthType, configureAmplify, getAuthFlowType, resolveInitialAuthType} from '../auth/amplify.config';

const AUTH_TYPE_STORAGE_KEY = 'app_auth_type';
const USER_ALIGNED_KEY = 'saml_user_aligned';


@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private _isAuthenticated = new BehaviorSubject<boolean | null>(null);
    public isAuthenticated$ = this._isAuthenticated.asObservable();
    private authType: AuthType;
    private isLoggingOut = false;
    private _userInfo = new BehaviorSubject<User | null>(null);
    public userInfo$ = this._userInfo.asObservable();
    private alignmentInFlight: Promise<boolean> | null = null;

    constructor(private userService: UserService, private router: Router, private subtenantService: SubtenantService) {

        if (!environment.samlEnabled) {
            this.authType = 'native';
        } else {
            const storedAuthType = localStorage.getItem(AUTH_TYPE_STORAGE_KEY);
            this.authType = resolveInitialAuthType(storedAuthType);
        }

        localStorage.setItem(AUTH_TYPE_STORAGE_KEY, this.authType);

        Hub.listen('auth', ({ payload }) => {
            switch (payload.event) {
                case 'signedIn':
                case 'signInWithRedirect':
                    this._isAuthenticated.next(true);
                    break;
                // v5: 'signOut'
                case 'signedOut':
                    if (!this.isLoggingOut) {
                        sessionStorage.removeItem(USER_ALIGNED_KEY);
                        this._isAuthenticated.next(false);
                    }
                    break;
                // v5: 'cognitoHostedUI_failure'
                case 'signInWithRedirect_failure':
                    console.error('AuthService: Cognito Hosted UI login failed:', payload.data);
                    this._isAuthenticated.next(false);
                    this.router.navigate(['/error-page']);
                    break;
            }
        });

        this.checkUser();
    }

    private configureAmplify(authType: AuthType) {
        this.authType = authType;
        localStorage.setItem(AUTH_TYPE_STORAGE_KEY, authType);
        configureAmplify(authType);
    }

    public async checkUser() {
        try {
            await getCurrentUser();
            this._isAuthenticated.next(true);
        } catch (error) {
            this._isAuthenticated.next(false);
        }
    }

    async getAccessToken(): Promise<string | null> {
        try {
            const session = await fetchAuthSession();
            return session.tokens?.accessToken?.toString() ?? null;
        } catch (err) {
            console.error('Errore nel recupero del token:', err);
            return null;
        }
    }

    async getIdToken(): Promise<string | null> {
        try {
            const session = await fetchAuthSession();
            return session.tokens?.idToken?.toString() ?? null;
        } catch (err) {
            console.error('Errore nel recupero dell\'id token:', err);
            return null;
        }
    }

    getUserInfo(): Observable<User | null> {
        if (this._userInfo.value) {
            return of(this._userInfo.value);
        }

        return from(getCurrentUser()).pipe(
            switchMap(() => {
                return this.userService.getUser().pipe(
                    tap(user => {
                        if (!user) {
                            console.warn('Utente autenticato ma non abilitato.');
                        }
                        this._userInfo.next(user || null);
                    }),
                    catchError(err => {
                        console.error('Errore nel recupero delle informazioni utente:', err);
                        return of(null);
                    })
                );
            }),
            catchError(err => {
                console.error('Errore nel recupero delle informazioni utente:', err);
                return of(null);
            })
        );
    }

    clearUserCache(): void {
        this._userInfo.next(null);
    }

    public getAmplifyUserInfo(): Observable<any> {
        return from(getCurrentUser()).pipe(
            catchError(err => {
                console.error('Errore nel recupero delle informazioni utente da Amplify:', err);
                return of(null);
            })
        );
    }

    async login(): Promise<void> {
        if (!environment.samlEnabled) {
            this.router.navigate(['/login']);
            return;
        }

        try {
            this.configureAmplify('saml');
            await signInWithRedirect({
                provider: {custom: environment.samlProviderName}
            });
        } catch (error) {
            console.error('AuthService: login error (SAML):', error);
        }
    }

    async loginWithCredentials(username: string, password: string): Promise<SignInOutput> {
        try {
            this.configureAmplify('native');

            // In v6 signIn() fallisce se esiste già una sessione attiva.
            if (await this.isAuthenticated()) {
                await signOut();
            }

            const output = await signIn({
                username,
                password,
                options: {authFlowType: getAuthFlowType('native')}
            });

            if (output.isSignedIn) {
                this._isAuthenticated.next(true);
            }
            return output;
        } catch (error) {
            console.error('AuthService: loginWithCredentials error:', error);
            throw error;
        }
    }

    async logout() {
        this.isLoggingOut = true;
        this.clearSessionState();

        try {
            const currentAuthType = this.authType;
            this._isAuthenticated.next(false);

            if (currentAuthType === 'saml' && environment.samlEnabled) {
                await signOut({global: true});

            } else {
                localStorage.removeItem(AUTH_TYPE_STORAGE_KEY);
                await signOut({global: true});

                const redirectUrl = environment.amplifyConfig.nativeAuth.oauth.redirectSignOut;
                window.location.assign(redirectUrl);
            }

        } catch (error) {
            console.error('AuthService: logout error:', error);
            this._isAuthenticated.next(false);
            window.location.assign('/');
        } finally {
            this.isLoggingOut = false;
        }
    }

    private clearSessionState(): void {
        sessionStorage.removeItem(USER_ALIGNED_KEY);
        this._userInfo.next(null);
        this.userService.clearUserCache();
        this.subtenantService.beginLogout();
    }

    async ensureUserAlignment(): Promise<boolean> {
        if (!environment.samlEnabled) {
            return true;
        }

        if (sessionStorage.getItem(USER_ALIGNED_KEY) === 'true') {
            return true;
        }

        if (this.alignmentInFlight) {
            return this.alignmentInFlight;
        }

        this.alignmentInFlight = this.doEnsureUserAlignment();
        try {
            return await this.alignmentInFlight;
        } finally {
            this.alignmentInFlight = null;
        }
    }

    private async doEnsureUserAlignment(): Promise<boolean> {
        let cognitoSub: string | undefined;
        let cognitoUserName: string | undefined;
        let email: string | undefined;

        try {
            const amplifyUser = await getCurrentUser();
            const session = await fetchAuthSession();
            const claims = session.tokens?.idToken?.payload ?? {};

            cognitoSub = amplifyUser.userId;
            cognitoUserName = amplifyUser.username;
            email = (claims['email'] as string) || (claims['cognito:username'] as string);
        } catch (error) {
            console.error('AuthService: impossibile leggere l\'utente Cognito per l\'allineamento:', error);
            return true;
        }

        if (!email || !cognitoSub) {
            return true;
        }

        try {
            const dbUser = await firstValueFrom(this.userService.getUserByEmail(new User({email})));

            if (!dbUser) {
                console.error('AuthService: utente autenticato ma non abilitato sul DB.');
                return false;
            }

            if (dbUser.id !== cognitoSub) {
                console.warn('AuthService: id utente non allineati, chiamo align-user.');
                const userToAlign = new User({
                    ...dbUser,
                    id: cognitoSub,
                    tempId: dbUser.id,
                    cognitoUserName
                });

                await firstValueFrom(this.userService.alignUser(userToAlign));
                this.userService.clearUserCache();

                // Il token di subtenant incorpora l'id utente: se già emesso va rigenerato.
                if (this.subtenantService.getSubtenantToken()) {
                    await firstValueFrom(this.subtenantService.refreshSubtenantToken());
                }

                console.log('AuthService: allineamento utente completato.');
            }

            sessionStorage.setItem(USER_ALIGNED_KEY, 'true');
            return true;
        } catch (error) {
            console.error('AuthService: allineamento utente fallito:', error);
            return false;
        }
    }

    async isAuthenticated(): Promise<boolean> {
        try {
            await getCurrentUser();
            return true;
        } catch {
            return false;
        }
    }

    async completeNewPasswordChallenge(newPassword: string): Promise<void> {
        try {

            const output = await confirmSignIn({challengeResponse: newPassword});
            if (output.isSignedIn) {
                this._isAuthenticated.next(true);
            }
        } catch (error) {
            console.error('AuthService: completeNewPasswordChallenge error:', error);
            throw error;
        }
    }

    async forgotPassword(username: string): Promise<void> {
        try {
            this.configureAmplify('native');
            await resetPassword({username});
        } catch (error) {
            console.error('AuthService: forgotPassword error:', error);
            throw error;
        }
    }

    async forgotPasswordSubmit(username: string, code: string, newPassword: string): Promise<void> {
        try {
            this.configureAmplify('native');
            await confirmResetPassword({username, confirmationCode: code, newPassword});
        } catch (error) {
            console.error('AuthService: forgotPasswordSubmit error:', error);
            throw error;
        }
    }
}
